import { CareTeamRole, TaskGroupIdentifier, TaskStatus } from '@bariendo/lib/constants';
import { getActiveConversationLeadTask } from '@bariendo/lib/conversation-lead';
import { referencesEqual } from '@bariendo/lib/services';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { createReference, formatHumanName, getReferenceString } from '@medplum/core';
import {
  Bundle,
  CareTeam,
  CareTeamParticipant,
  Practitioner,
  PractitionerRole,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTimelineContext } from './useTimelineContext';
import { useSubscriptionWithLimitHandling } from '@/services/subscriptions';

export interface CareTeamProviderItem {
  id: string;
  value?: string | undefined;
  callable: boolean;
  conversationLead: boolean;
  provider?: Practitioner;
  providerReference: Reference<Practitioner>;
  providerRole?: PractitionerRole;
}

export interface UseCareTeamManagementResult {
  providers: CareTeamProviderItem[];
  conversationLeadExists: boolean;
  isSaving: boolean;
  addCareTeamProvider: (item: { practitioner: Practitioner }) => Promise<void>;
  removeCareTeamProvider: (reference: Reference<Practitioner>) => Promise<void>;
  setCareTeamProviderAsConversationLead: (reference: Reference<Practitioner>) => Promise<void>;
}

// Utility functions
const getParticipantRoles = (participant: CareTeamParticipant): string[] => {
  return (participant.role || []).flatMap((role) => (role.coding || []).map((coding) => coding.code || ''));
};

const findParticipantByReference = (
  participants: CareTeamParticipant[],
  reference: Reference<Practitioner>
): CareTeamParticipant | undefined => {
  const referenceString = getReferenceString(reference);
  return participants.find((p) => p.member && getReferenceString(p.member) === referenceString);
};

export function useCareTeamManagement(): UseCareTeamManagementResult {
  const medplum = useMedplum();
  const { careTeamContext, patientContext, timelineContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const { careTeam, careTeamProviders, refreshCareTeam } = careTeamContext ?? {};
  const { pauseLiveUpdates, resumeLiveUpdates } = timelineContext;
  const [providers, setProviders] = useState<CareTeamProviderItem[]>([]);
  const [isSaving, setSaving] = useState(false);
  const [clTask, setClTask] = useState<Task | undefined>(undefined);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    (async () => {
      if (!patient) {
        setClTask(undefined);
        return;
      }
      const clTask = await getActiveConversationLeadTask(medplum, createReference(patient));
      setClTask(clTask);
    })();
  }, [patient, medplum]);

  const handleConversationLeadUpdate = useCallback(
    async (bundle: Bundle) => {
      if (!patient) {
        setClTask(undefined);
        return;
      }

      const newTask = bundle.entry?.[1]?.resource as Task | undefined;

      const isSameTask = newTask && newTask.id === clTask?.id;

      if (isSameTask && newTask.status !== TaskStatus.IN_PROGRESS) {
        setClTask(undefined);
      } else if (!isSameTask && newTask?.status === TaskStatus.IN_PROGRESS) {
        setClTask(newTask);
      }
    },
    [patient, clTask]
  );

  const subscriptionCriteria = useMemo(() => {
    if (!patient) return '';
    const queryParams = new URLSearchParams({
      'group-identifier': TaskGroupIdentifier.CONVERSATION_LEAD,
      patient: getReferenceString(createReference(patient)),
    });
    return `Task?${queryParams.toString()}`;
  }, [patient]);

  useSubscriptionWithLimitHandling(subscriptionCriteria, handleConversationLeadUpdate);

  useEffect(() => {
    (async () => {
      if (!careTeam) {
        setProviders([]);
        return;
      }

      if (!careTeam.participant || careTeam.participant.length === 0) {
        setProviders([]);
        return;
      }

      // Wait for providers to load if participants exist but providers haven't loaded yet
      if (!careTeamProviders || careTeamProviders.length === 0) {
        return;
      }
      const providerRoles = await medplum.searchResources('PractitionerRole', {
        practitioner: careTeamProviders.map((p) => getReferenceString(p)).join(','),
      });

      // Build base provider list
      let result = (careTeam?.participant || []).map((participant) => {
        const memberRef = participant.member ? getReferenceString(participant.member) : '';
        const practitioner = careTeamProviders.find((p) => getReferenceString(p) === memberRef);
        const participantRoles = getParticipantRoles(participant);
        const providerRole = providerRoles.find((r) =>
          r.practitioner ? getReferenceString(r.practitioner) === memberRef : undefined
        );

        return {
          id: participant.member ? getReferenceString(participant.member) || 'unknown' : 'unknown',
          value: practitioner ? formatHumanName(practitioner.name?.[0]) : undefined,
          callable: participantRoles.includes(CareTeamRole.CALLABLE),
          conversationLead: false, // will be filled by active Task below
          providerReference: participant.member as Reference<Practitioner>,
          provider: practitioner,
          providerRole,
        } as CareTeamProviderItem;
      });

      // Resolve active Conversation Lead Task and mark the owner
      if (clTask) {
        result = result.map((item) => {
          const isLead = referencesEqual(item.providerReference, clTask.owner!);
          return {
            ...item,
            conversationLead: isLead,
          };
        });
      }

      setProviders(result);
    })();
  }, [medplum, careTeam, careTeamProviders, patient?.id, clTask]);

  const conversationLeadExists = useMemo(() => providers.some((item) => item.conversationLead), [providers]);

  const updateCareTeam = useCallback(
    async (newParticipants: CareTeamParticipant[]) => {
      if (!careTeam) return;

      // Pause timeline live updates to prevent unnecessary requests during account propagation
      pauseLiveUpdates();

      try {
        await medplum.updateResource<CareTeam>({ ...careTeam, participant: newParticipants });
        await refreshCareTeam?.();

        // Wait for server-side account propagation to complete (careteam-subscription bot)
        timeoutRef.current = setTimeout(() => {
          resumeLiveUpdates();
          timeoutRef.current = null;
        }, 3000);
      } catch (error) {
        alert.error(`Couldn't update the care team: ${error instanceof Error ? error.message : 'Unknown error'}`);
        resumeLiveUpdates();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    },
    [careTeam, medplum, refreshCareTeam, pauseLiveUpdates, resumeLiveUpdates]
  );

  const executeOperation = useCallback(
    async (operation: () => Promise<void>) => {
      if (isSaving) return;
      setSaving(true);
      try {
        await operation();
      } finally {
        setSaving(false);
      }
    },
    [isSaving]
  );

  const removeCareTeamProvider = useCallback(
    async (reference: Reference<Practitioner>) => {
      await executeOperation(async () => {
        const referenceString = getReferenceString(reference);
        const newParticipants = (careTeam?.participant || []).filter(
          (p) => !p.member || getReferenceString(p.member) !== referenceString
        );
        await updateCareTeam(newParticipants);
      });
    },
    [careTeam, executeOperation, updateCareTeam]
  );

  const addCareTeamProvider = useCallback(
    async (item: { practitioner: Practitioner }) => {
      await executeOperation(async () => {
        if (!careTeam) return; // Only proceed if careTeam exists

        const participants = [...(careTeam.participant || [])];

        // Check if practitioner already exists
        const practitionerRef = getReferenceString(item.practitioner);
        const existingParticipant = participants.find((p) =>
          p.member ? getReferenceString(p.member) === practitionerRef : false
        );

        if (existingParticipant) {
          await updateCareTeam(participants);
          return;
        }

        // Add new participant
        const newParticipant: CareTeamParticipant = {
          member: { reference: practitionerRef, display: item.practitioner.name?.[0]?.family },
          role: [],
        };
        participants.push(newParticipant);

        await updateCareTeam(participants);
      });
    },
    [careTeam, executeOperation, updateCareTeam]
  );

  const setCareTeamProviderAsConversationLead = useCallback(
    async (reference: Reference<Practitioner>) => {
      await executeOperation(async () => {
        if (!careTeam) return;

        const participants = careTeam.participant || [];
        const targetParticipant = findParticipantByReference(participants, reference);
        if (!targetParticipant) return;

        await fetchBot(medplum, 'set-conversation-lead', {
          patientRef: createReference(patient!),
          providerRef: reference,
        });
      });
    },
    [careTeam, executeOperation, medplum, patient]
  );

  return {
    providers,
    conversationLeadExists,
    isSaving,
    addCareTeamProvider,
    removeCareTeamProvider,
    setCareTeamProviderAsConversationLead,
  };
}
